import React, { useState, useEffect, useMemo } from "react";
import {
  message,
  Typography,
  Card,
  Col,
  Row,
  Statistic,
  Alert,
  Layout,
  Grid,
  Progress,
  Spin,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import Leaderboard from "./leaderboardSidebar";
import LeaderBoardComp from "../tester-teamlead/leaderBoard";
import useLogout from "../useLogout";
import "./profileRoleBased.css";

const { Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const defaultDailyOTPCount = process.env.REACT_APP_API_dialyOTPs;
const defaultMonthlyOTPCount = process.env.REACT_APP_API_MonthlyOTPs;
const defaultPreviousMonthOTPCount = process.env.REACT_APP_API_PreviousMonthCount;
const bonusApi = process.env.REACT_APP_API_BonusURL;
const defaultDuplicateMonthlyCount = process.env.REACT_APP_API_DuplicateOTPs;
const configSettingAPI = process.env.REACT_APP_ConfigSetting;
const bannedAppCountApi =
  "https://gj2fi00268.execute-api.us-east-1.amazonaws.com/dev/total-banned-app-otp-count";

const Testers = ({ apiUrls, variant }) => {
  const dailyOTPCount = apiUrls?.dailyOTPCount ?? defaultDailyOTPCount;
  const monthlyOTPCount = apiUrls?.monthlyOTPCount ?? defaultMonthlyOTPCount;
  const previousMonthOTPCount = apiUrls?.previousMonthOTPCount ?? defaultPreviousMonthOTPCount;
  const duplicateMonthlyCount = apiUrls?.duplicateMonthlyCount ?? defaultDuplicateMonthlyCount;
  const { Title } = Typography;
  const [messageApi, contextHolder] = message.useMessage();
  const [dailyCount, setDailyCount] = useState(0);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [previousMonthCount, setPreviousMonthCount] = useState(0);
  const [duplicateMonthCount, setDuplicateMonthCount] = useState(0);
  const [bonusUSD, setBonusUSD] = useState();
  const [lowerLimitPayout, setLowerLimitPayout] = useState();
  const [loading, setLoading] = useState(true);
  // const [minChargesPerMonthUSD, setMinChargesPerMonthUSD] = useState(35.99);
  const [bannedCount, setBannedCount] = useState(0);
  const [paymentRate, setPaymentRate] = useState(0.01);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : {};

  const screens = useBreakpoint();

  const logout = useLogout();

  useEffect(() => {
    fetchOTPData();
  }, []);

  const fetchOTPData = async () => {
    try {
      const userId = user.userId;
      const automationIds = [669, 1197, 1198, 1199, 1200, 1201];
      if (automationIds.includes(userId)) {
        setPaymentRate(0.005);
      }

      const [
        dailyResponse,
        bannedOtpCountResponse,
        previousMonthCountResponse,
        bonusResponse,
        monthlyResponse,
        monthlyDuplicateResponse,
        configSettingResponse,
      ] = await Promise.all([
        fetch(`${dailyOTPCount}/${userId}`),
        fetch(`${bannedAppCountApi}/${userId}`),
        fetch(`${previousMonthOTPCount}/${userId}`),
        fetch(`${bonusApi}/${userId}`),
        fetch(`${monthlyOTPCount}/${userId}`),
        fetch(`${duplicateMonthlyCount}/${userId}`),
        fetch(configSettingAPI),
      ]);

      const [
        dailyData,
        bannedOtpData,
        prevMonthData,
        bonusData,
        monthlyData,
        monthlyDuplicateData,
        configData,
      ] = await Promise.all([
        dailyResponse.json(),
        bannedOtpCountResponse.json(),
        previousMonthCountResponse.json(),
        bonusResponse.json(),
        monthlyResponse.json(),
        monthlyDuplicateResponse.json(),
        configSettingResponse.json(),
      ]);

      if (dailyData.inactive || bannedOtpData.inactive || prevMonthData.inactive ||
          monthlyData.inactive || monthlyDuplicateData.inactive) {
        logout();
        return;
      }

      setDailyCount(dailyData.otp_today);
      setBannedCount(bannedOtpData.total_count);
      setPreviousMonthCount(prevMonthData.otp_current_month);

      const bodyObject = JSON.parse(bonusData.body);
      const currentBonus = bodyObject ? bodyObject.current_month_bonus : bonusData.current_month_bonus;
      setBonusUSD(currentBonus);

      setMonthlyCount(monthlyData.otp_current_month);
      setDuplicateMonthCount(monthlyDuplicateData.duplicate_otp_current_month);

      const { lower_limit_payout } = configData[0];
      setLowerLimitPayout(lower_limit_payout || 0);
    } catch (error) {
      console.error("Error fetching OTP data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateValidOTPsAndPayment = (monthlyCount, duplicateMonthCount) => {
    const validOTPs = monthlyCount - duplicateMonthCount;
    let payment = validOTPs * paymentRate;
    console.log("payment", payment);

    return { validOTPs, payment };
  };

  const { validOTPs, payment } = useMemo(
    () => calculateValidOTPsAndPayment(monthlyCount, duplicateMonthCount),
    [monthlyCount, duplicateMonthCount]
  );

  const isRoleBased = variant === "roleBased";
  const progressPercent = Math.min(Math.round((validOTPs / 5000) * 100), 100);

  if (isRoleBased) {
    return (
      <div className="profile-rb">
        {contextHolder}
        <div className="profile-rb-main">
          <h1 className="profile-rb-title">Your Dashboard</h1>
          <p className="profile-rb-subtitle">Overview of your testing activity and earnings</p>

          <div className="profile-rb-alert profile-rb-alert-info">
            If your valid OTP count for the month is less than 5000, the payment will be sent next month.
          </div>
          <div className="profile-rb-alert profile-rb-alert-warning">
            Payment is made only to those generating a minimum of 5000 OTPs per month. Failure to maintain this for three consecutive months may result in account suspension.
          </div>

          <Spin spinning={loading} size="large">
          <div className="profile-rb-stats">
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Tests Today</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-success">{dailyCount}</div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Tests This Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-success">{monthlyCount}</div>
            </div>
            <div className="profile-rb-stat-card profile-rb-stat-danger">
              <div className="profile-rb-stat-label">Duplicates This Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-danger">{duplicateMonthCount}</div>
            </div>
            <div className="profile-rb-stat-card profile-rb-stat-success">
              <div className="profile-rb-stat-label">Valid Tests This Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-success">{validOTPs}</div>
            </div>
            <div className="profile-rb-stat-card profile-rb-stat-warning">
              <div className="profile-rb-stat-label">Total Payment This Month</div>
              <div className="profile-rb-stat-value">
                ${(payment + (bonusUSD || 0)).toFixed(2)}
                <span className="profile-rb-stat-suffix">USD</span>
              </div>
              <div style={{ marginTop: 8, fontSize: "0.75rem", color: "#64748b" }}>
                Base: ${payment.toFixed(2)} · Bonus: ${(bonusUSD || 0).toFixed(2)}
              </div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Previous Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-warning">{previousMonthCount}</div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Rate</div>
              <div className="profile-rb-stat-value" style={{ fontSize: "1rem" }}>
                ${paymentRate}/OTP
              </div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Target Progress (5000)</div>
              <div className="profile-rb-stat-value" style={{ color: validOTPs >= 5000 ? "#059669" : "#dc2626" }}>
                {validOTPs}
                <span className="profile-rb-stat-suffix">/ 5000</span>
              </div>
              <div className="profile-rb-progress-wrap">
                <Progress
                  percent={progressPercent}
                  status={validOTPs >= 5000 ? "success" : "active"}
                  strokeColor={validOTPs >= 5000 ? "#10b981" : "#38bdf8"}
                  size="small"
                  showInfo={false}
                />
              </div>
            </div>
          </div>
          </Spin>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {contextHolder}
      <Content
        style={{ padding: "0 18px", minHeight: "100vh", background: "#fff" }}
      >
        <Title level={3}>Tester Statistics</Title>
        {/* <Alert
          message={`For every OTP from banned Apps(WhatsApp, Facebook, Instagram, Snapchat, Monty, Twilio, Bird, SinchVerify) $0.02 will be deducted from your monthly payment`}
          type="error"
          style={{ marginBottom: "16px" }}
        /> */}
        {/* {payment < minChargesPerMonthUSD ? ( */}
          <Alert
            message={`If your valid OTP count for the month is less than 5000, the payment for it will be sent next month`}
            banner
            style={{ marginBottom: "16px" }}
          />
        {/* ) : null} */}
         <Alert
          message={`Payment will be made ONLY to those generating minimum of 5000 OTPs per month`}
          banner
          style={{ marginBottom: "16px" }}
        />

        <Alert
          message={`Failure to maintain a monthly OTP count of 5000 for three consecutive months will result in account suspension.`}
          banner
          style={{ marginBottom: "16px" }}
        />

        <Spin spinning={loading} size="large">
        <Row gutter={[16, 16]} justify="center">
          <Col xs={24}>
            <Card
              title="Your Profile"
              bordered={false}
              style={{
                border: "1px solid #d8d8d8",
                borderRadius: "8px",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    bordered={false}
                    style={{
                      border: "1px solid #d8d8d8",
                      borderRadius: "8px",
                      minHeight: screens.md ? "8.5rem" : "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}
                  >
                    <Statistic
                      title="Total Received Tests Today"
                      value={dailyCount}
                      valueStyle={{ color: "#3f8600",  fontSize: "20px" }}
                      prefix={<ArrowUpOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    bordered={false}
                    style={{
                      border: "1px solid #d8d8d8",
                      borderRadius: "8px",
                      minHeight: screens.md ? "8.5rem" : "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}
                  >
                    <Statistic
                      title="Received Tests this Month"
                      value={monthlyCount}
                      valueStyle={{ color: "#3f8600",  fontSize: "20px" }}
                      prefix={<ArrowUpOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    bordered={false}
                    style={{
                      border: "1px solid #d8d8d8",
                      borderRadius: "8px",
                      minHeight: screens.md ? "8.5rem" : "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}
                  >
                    <Statistic
                      title="Duplicate Tests this Month"
                      value={duplicateMonthCount}
                      valueStyle={{ color: "#cf1322",  fontSize: "20px" }}
                      prefix={<ArrowDownOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card
                    bordered={false}
                    style={{
                      border: "1px solid #d8d8d8",
                      borderRadius: "8px",
                      minHeight: screens.md ? "8.5rem" : "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }}
                  >
                    <Statistic
                      title="Valid Tests for this Month"
                      value={validOTPs}
                      valueStyle={{ color: "#3f8600",  fontSize: "20px" }}
                      prefix={<ArrowUpOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
             
<Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
  <Col xs={24} sm={12} md={6}>
    <Card
      bordered={false}
      style={{
        border: "1px solid #d8d8d8",
        borderRadius: "8px",
        minHeight: screens.md ? "8.5rem" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}
    >
      <div style={{ textAlign: "center" }}>
        <Statistic
          title="Total Payment This Month"
          value={payment + (bonusUSD || 0)}
          precision={3}
          valueStyle={{ color: "#3f8600", fontSize: "18px", fontWeight: "bold" }}
          suffix="$(USD)"
        />
        <div 
          style={{ 
            borderTop: "1px dashed #d9d9d9", 
            margin: "12px 0", 
            paddingTop: "8px",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap"
          }}
        >
          <div style={{ minWidth: "45%" }}>
            <Statistic
              title={<span style={{ fontSize: "12px" }}>Base</span>}
              value={payment}
              precision={3}
              valueStyle={{ color: "#3f8600", fontSize: "14px" }}
              suffix="$"
            />
          </div>
          <div style={{ minWidth: "45%" }}>
            <Statistic
              title={<span style={{ fontSize: "12px" }}>Bonus</span>}
              value={bonusUSD || 0}
              precision={3}
              valueStyle={{ color: "#FFC000", fontSize: "14px" }}
              suffix="$"
            />
          </div>
        </div>
      </div>
    </Card>
  </Col>
  <Col xs={24} sm={12} md={6}>
    <Card
      bordered={false}
      style={{
        border: "1px solid #d8d8d8",
        borderRadius: "8px",
        minHeight: screens.md ? "8.5rem" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}
    >
      <Statistic
        title="Total Received Tests Previous Month"
        value={previousMonthCount}
        valueStyle={{ color: "#FFC000", fontSize: "18px" }}
      />
    </Card>
  </Col>
  <Col xs={24} sm={12} md={6}>
    <Card
      bordered={false}
      style={{
        border: "1px solid #d8d8d8",
        borderRadius: "8px",
        minHeight: screens.md ? "8.5rem" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}
    >
      <Statistic
        title={"Per OTP Payment Rate"}
        value={` ${paymentRate} dollars per OTP`}
        precision={2}
        valueStyle={{ color: "#3f8600", fontSize: "14px" }}
      />
    </Card>
  </Col>
  <Col xs={24} sm={12} md={6}>
    <Card
      bordered={false}
      style={{
        border: "1px solid #d8d8d8",
        borderRadius: "8px",
        minHeight: screens.md ? "8.5rem" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}
    >
      <Statistic
        title="Valid Tests this Month (Target: 5000)"
        value={validOTPs}
        valueStyle={{ 
          color: validOTPs >= 5000 ? "#3f8600" : "#ff4d4f", 
          fontSize: "18px" 
        }}
        suffix={<span style={{ fontSize: "14px" }}>/ 5000</span>}
      />
      <div style={{ marginTop: "8px", textAlign: "center" }}>
        <Progress 
          percent={Math.min(Math.round((validOTPs / 5000) * 100), 100)} 
          status={validOTPs >= 5000 ? "success" : "active"}
          strokeColor={validOTPs >= 5000 ? "#3f8600" : "#1890ff"}
          size="small"
        />
      </div>
    </Card>
  </Col>
</Row>            </Card>
          </Col>
        </Row>
        </Spin>

        {!screens.md && (
          <div style={{ marginTop: "16px" }}>
            <LeaderBoardComp />
          </div>
        )}
      </Content>

      {/* {screens.md && (
        <Sider width={250} style={{ background: "#fff" }}>
          <Leaderboard />
        </Sider>
      )} */}
    </Layout>
  );
};

export default Testers;
